import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Settings, Package, Trophy, TrendingDown } from 'lucide-react';
import config from '../config';
import { useSelectedMetrics, getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
/**
 * Composant modulaire pour afficher les tops et flops catégories produits
 * Top catégories = 5 catégories les plus vendeuses
 * Flop catégories = 5 catégories les plus cliquées sans vente
 *
 * Ce composant charge ses propres données depuis l'endpoint modulaire
 */
const TopFlopCategoriesTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  token,
  setError,
  /**
   * Les campagnes retenues par les filtres par identifiant — labels et type.
   *
   * `campaignTypeFilter` est envoyé à la route, qui ne sait pas l'appliquer : une catégorie
   * agrège les lignes de plusieurs campagnes et n'en retient qu'une. La restriction part donc
   * avec la requête Google Ads, sous forme d'identifiants.
   */
  campaignIds = null,
  preloadedData = null,
  // Props pour filtrage par labels
  selectedLabelsFilter = [],
  campaignMatchesLabelsFilter = () => true,
  // Props pour comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'topflop_categories',
  loadingComparison = false
}) => {
  const selectedMetrics = useSelectedMetrics(selectedAccount?.id || 'global');
  const [showMetricsConfigTop, setShowMetricsConfigTop] = useState(false);
  const [showMetricsConfigFlop, setShowMetricsConfigFlop] = useState(false);

  // Configuration des métriques séparées pour Top et Flop
  const topMetrics = useComponentMetrics('topflop_categories_top', selectedAccount?.id || 'global');
  const flopMetrics = useComponentMetrics('topflop_categories_flop', selectedAccount?.id || 'global');

  // Fonctions de visibilité des métriques pour Top et Flop
  const isTopMetricVisible = useCallback((metricKey) => {
    return topMetrics.metrics.includes(metricKey);
  }, [topMetrics.metrics]);

  const isFlopMetricVisible = useCallback((metricKey) => {
    return flopMetrics.metrics.includes(metricKey);
  }, [flopMetrics.metrics]);

  // États locaux pour les données
  const [topCategories, setTopCategories] = useState([]);
  const [flopCategories, setFlopCategories] = useState([]);
  const [previousTopCategories, setPreviousTopCategories] = useState([]);
  const [previousFlopCategories, setPreviousFlopCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPreviousPeriod, setLoadingPreviousPeriod] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);





  // Charger les données quand le composant est monté ou que les dépendances changent
  useEffect(() => {
    if (!isVisible) return;

    console.log('📊 [TOP-FLOP-CATEGORIES] useEffect déclenché:', {
      selectedAccount: selectedAccount?.id,
      selectedPeriod,
      hasToken: !!token,
      hasPreloadedData: !!preloadedData,
      compareEnabled,
      comparisonType
    });

    // Si des données préchargées sont disponibles ET qu'aucun filtre n'est actif
    // `campaignIds` dans la garde : les données préchargées portent tout le compte, et s'en
    // servir sous un filtre afficherait le compte entier derrière un en-tête filtré.
    const sansFiltreCampagnes = !campaignIds || campaignIds.length === 0;
    if (preloadedData && preloadedData.success && !selectedCampaignFilter && !selectedCampaignTypeFilter && sansFiltreCampagnes) {
      const hasPreloadedCategories = (preloadedData.data?.topCategories?.length > 0) || (preloadedData.data?.flopCategories?.length > 0);

      console.log('📊 [TOP-FLOP-CATEGORIES] Vérification données préchargées:', {
        topCategoriesCount: preloadedData.data?.topCategories?.length || 0,
        flopCategoriesCount: preloadedData.data?.flopCategories?.length || 0,
        hasPreloadedCategories
      });

      if (hasPreloadedCategories) {
        console.log(`📊 [TOP-FLOP-CATEGORIES] Utilisation données préchargées (sans comparaison pour éviter appels dupliqués)`);
        setTopCategories(preloadedData.data.topCategories || []);
        setFlopCategories(preloadedData.data.flopCategories || []);
        setIsDataLoaded(true);
        setLoading(false);
        // NOTE: On ne charge PAS les données de période précédente ici pour éviter les appels API dupliqués
        // La comparaison sera disponible après un rechargement manuel (changement filtre/période)
        setPreviousTopCategories([]);
        setPreviousFlopCategories([]);
        return;
      }
      console.log('📊 [TOP-FLOP-CATEGORIES] PreloadedData vide, chargement depuis API...');
    }

    if (!selectedAccount || !selectedPeriod || !token) {
      console.log('🔍 [TOP-FLOP-CATEGORIES-MODULAR] Paramètres manquants:', {
        selectedAccount: !!selectedAccount,
        selectedPeriod: !!selectedPeriod,
        token: !!token
      });
      return;
    }

    const loadTopFlopCategories = async () => {
      try {
        setLoading(true);

        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          campaignFilter: selectedCampaignFilter || null,
          campaignTypeFilter: selectedCampaignTypeFilter || null,
          campaignIds: campaignIds && campaignIds.length > 0 ? campaignIds : null,
          limit: 5
        };

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/top-flop-categories`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          setTopCategories(response.data.data.topCategories || []);
          setFlopCategories(response.data.data.flopCategories || []);
          setIsDataLoaded(true);

          // Charger les données de la période précédente si comparaison activée
          if (compareEnabled && comparisonType) {
            let currentStartDate = customStartDate;
            let currentEndDate = customEndDate;
            if (!currentStartDate || !currentEndDate) {
              const dateRange = getDateRangeFromPeriod(selectedPeriod);
              currentStartDate = dateRange.startDate;
              currentEndDate = dateRange.endDate;
            }
            const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
            console.log(`📊 [TOP-FLOP-CATEGORIES] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

            try {
              const prevRequestBody = {
                sources: [selectedAccount.id],
                period: 'CUSTOM',
                customStartDate: prevPeriod.startDate,
                customEndDate: prevPeriod.endDate,
                campaignFilter: selectedCampaignFilter || null,
                campaignTypeFilter: selectedCampaignTypeFilter || null,
                campaignIds: campaignIds && campaignIds.length > 0 ? campaignIds : null,
                limit: 5
              };

              const prevResponse = await axios.post(
                `${config.API_URL}/api/dashboard/components/top-flop-categories`,
                prevRequestBody,
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );

              if (prevResponse.data.success) {
                console.log(`📊 [TOP-FLOP-CATEGORIES] Données période précédente chargées:`, {
                  topCount: prevResponse.data.data.topCategories?.length || 0,
                  flopCount: prevResponse.data.data.flopCategories?.length || 0
                });
                setPreviousTopCategories(prevResponse.data.data.topCategories || []);
                setPreviousFlopCategories(prevResponse.data.data.flopCategories || []);
              }
            } catch (prevError) {
              console.warn(`⚠️ [TOP-FLOP-CATEGORIES] Erreur chargement période précédente:`, prevError.message);
              setPreviousTopCategories([]);
              setPreviousFlopCategories([]);
            }
          } else {
            setPreviousTopCategories([]);
            setPreviousFlopCategories([]);
          }
        } else {
          console.error('❌ [TOP-FLOP-CATEGORIES-MODULAR] API retourne success=false:', response.data);
          setError('Erreur lors du chargement des tops/flops catégories');
        }
      } catch (error) {
        console.error('❌ [TOP-FLOP-CATEGORIES-MODULAR] Erreur chargement:', error);
        console.error('❌ [TOP-FLOP-CATEGORIES-MODULAR] Détails:', error.response?.data || error.message);
        setError('Erreur lors du chargement des tops/flops catégories');
      } finally {
        setLoading(false);
      }
    };

    loadTopFlopCategories();
  }, [isVisible, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, campaignIds, token, setError, preloadedData, compareEnabled, comparisonType]);

  // Charger les données de comparaison séparément quand compareEnabled change
  useEffect(() => {
    if (!isVisible) return;

    const loadComparisonDataOnly = async () => {
      if (!compareEnabled || !comparisonType || !selectedAccount || !token || !isDataLoaded) {
        if (!compareEnabled) {
          setPreviousTopCategories([]);
          setPreviousFlopCategories([]);
        }
        return;
      }

      // Calculer les dates de la période actuelle
      let currentStartDate = customStartDate;
      let currentEndDate = customEndDate;
      if (!currentStartDate || !currentEndDate) {
        const dateRange = getDateRangeFromPeriod(selectedPeriod);
        currentStartDate = dateRange.startDate;
        currentEndDate = dateRange.endDate;
      }

      const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
      console.log(`📊 [TOP-FLOP-CATEGORIES] Chargement données comparaison (effet séparé): ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

      setLoadingPreviousPeriod(true);
      try {
        const prevRequestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate,
          campaignFilter: selectedCampaignFilter || null,
          campaignTypeFilter: selectedCampaignTypeFilter || null,
          campaignIds: campaignIds && campaignIds.length > 0 ? campaignIds : null,
          limit: 5
        };

        const prevResponse = await axios.post(
          `${config.API_URL}/api/dashboard/components/top-flop-categories`,
          prevRequestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (prevResponse.data.success) {
          console.log(`📊 [TOP-FLOP-CATEGORIES] Données comparaison chargées:`, {
            topCount: prevResponse.data.data.topCategories?.length || 0,
            flopCount: prevResponse.data.data.flopCategories?.length || 0
          });
          setPreviousTopCategories(prevResponse.data.data.topCategories || []);
          setPreviousFlopCategories(prevResponse.data.data.flopCategories || []);
        }
      } catch (error) {
        console.warn(`⚠️ [TOP-FLOP-CATEGORIES] Erreur chargement données comparaison:`, error.message);
        setPreviousTopCategories([]);
        setPreviousFlopCategories([]);
      } finally {
        setLoadingPreviousPeriod(false);
      }
    };

    loadComparisonDataOnly();
  }, [isVisible, compareEnabled, comparisonType, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, campaignIds, token, isDataLoaded]);

  // Filtrer les catégories par labels
  const filteredTopCategories = useMemo(() => {
    if (selectedLabelsFilter.length === 0) {
      return topCategories;
    }
    return topCategories.filter(category => {
      const categoryCampaignId = category.campaignId || category.campaign_id;
      return campaignMatchesLabelsFilter(categoryCampaignId);
    });
  }, [topCategories, selectedLabelsFilter, campaignMatchesLabelsFilter]);

  const filteredFlopCategories = useMemo(() => {
    if (selectedLabelsFilter.length === 0) {
      return flopCategories;
    }
    return flopCategories.filter(category => {
      const categoryCampaignId = category.campaignId || category.campaign_id;
      return campaignMatchesLabelsFilter(categoryCampaignId);
    });
  }, [flopCategories, selectedLabelsFilter, campaignMatchesLabelsFilter]);

  // Calcul des totaux pour les top catégories
  const topTotals = useMemo(() => {
    if (!filteredTopCategories.length) return null;

    const baseTotals = filteredTopCategories.reduce((acc, category) => ({
      clicks: acc.clicks + (category.clicks || 0),
      conversions: acc.conversions + (category.conversions || 0),
      cost: acc.cost + (category.cost || 0),
      conversionsValue: acc.conversionsValue + (category.conversionsValue || 0)
    }), { clicks: 0, conversions: 0, cost: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [filteredTopCategories]);

  // Calcul des totaux pour les flop catégories
  const flopTotals = useMemo(() => {
    if (!filteredFlopCategories.length) return null;

    const baseTotals = filteredFlopCategories.reduce((acc, category) => ({
      clicks: acc.clicks + (category.clicks || 0),
      cost: acc.cost + (category.cost || 0),
      conversions: acc.conversions + (category.conversions || 0),
      conversionsValue: acc.conversionsValue + (category.conversionsValue || 0)
    }), { clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [filteredFlopCategories]);

  // Calcul des totaux de la période précédente pour les top catégories
  const previousTopTotals = useMemo(() => {
    if (!compareEnabled || !previousTopCategories.length) return null;

    const baseTotals = previousTopCategories.reduce((acc, category) => ({
      clicks: acc.clicks + (category.clicks || 0),
      conversions: acc.conversions + (category.conversions || 0),
      cost: acc.cost + (category.cost || 0),
      conversionsValue: acc.conversionsValue + (category.conversionsValue || 0)
    }), { clicks: 0, conversions: 0, cost: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousTopCategories]);

  // Calcul des totaux de la période précédente pour les flop catégories
  const previousFlopTotals = useMemo(() => {
    if (!compareEnabled || !previousFlopCategories.length) return null;

    const baseTotals = previousFlopCategories.reduce((acc, category) => ({
      clicks: acc.clicks + (category.clicks || 0),
      cost: acc.cost + (category.cost || 0),
      conversions: acc.conversions + (category.conversions || 0),
      conversionsValue: acc.conversionsValue + (category.conversionsValue || 0)
    }), { clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousFlopCategories]);

  // Debug des conditions d'affichage
  console.log('📊 [TOP-FLOP-CATEGORIES] État:', {
    loading,
    isDataLoaded,
    topCategoriesCount: topCategories.length,
    flopCategoriesCount: flopCategories.length,
    previousTopCategoriesCount: previousTopCategories.length,
    previousFlopCategoriesCount: previousFlopCategories.length,
    selectedAccount: selectedAccount?.id,
    selectedPeriod,
    hasToken: !!token,
    compareEnabled
  });

  // Attendre que les paramètres nécessaires soient disponibles
  if (!selectedAccount || !token) {
    console.log('📊 [TOP-FLOP-CATEGORIES] En attente des paramètres (compte/token)');
    return null;
  }

  // Ne pas afficher si pas de données et pas en chargement (mais seulement si on a essayé de charger)
  if (!loading && !isDataLoaded && !preloadedData) {
    console.log('📊 [TOP-FLOP-CATEGORIES] Masqué: pas de données et pas en chargement');
    return null;
  }

  // Masquer complètement si aucune catégorie trouvée (après filtrage par labels)
  const hasNoCategories = !loading && isDataLoaded && filteredTopCategories.length === 0 && filteredFlopCategories.length === 0;

  if (hasNoCategories) {
    console.log('📊 [TOP-FLOP-CATEGORIES] Masqué: aucune catégorie trouvée');
    return null;
  }

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Package size={16} />&nbsp;Top & Flop Catégories Produits</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Top/Flop Catégories"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <div className="table-container" style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}>
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              Chargement des tops & flops catégories...<br /><br />
            </div>
          ) : compareEnabled && (loadingComparison || loadingPreviousPeriod) ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          ) : (
            <div className="paire-top-flop" style={{ display: 'flex', gap: '20px' }}>
              {/* Top Catégories */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 15px 0' }}>
                  <h5 style={{ margin: 0, color: '#2e7d32', fontSize: '1.1em' }}>
                    <Trophy size={16} />&nbsp;Top 5 Catégories (Plus vendeuses)
                  </h5>
                  {userRole !== 'CLIENT' && (
                    <button
                      onClick={() => setShowMetricsConfigTop(true)}
                      className="metrics-settings-btn"
                      title="Configurer les métriques"
                      style={{ padding: '4px', borderRadius: '4px' }}
                    >
                      <Settings size={14} />
                    </button>
                  )}
                </div>
                <div className="campaigns-table-wrapper">
                  <table className="campaigns-table" key={selectedMetrics.join(",")} style={{ tableLayout: "auto" }}>
                    <thead>
                      <tr>
                        <th>Catégorie</th>
                        {isTopMetricVisible('impressions') && <th className="numeric">Impr.</th>}
                        {isTopMetricVisible('clics') && <th className="numeric">Clics</th>}
                        {isTopMetricVisible('ctr') && <th className="numeric">CTR</th>}
                        {isTopMetricVisible('cpc') && <th className="numeric">CPC</th>}
                        {isTopMetricVisible('cout') && <th className="numeric">Coût</th>}
                        {isTopMetricVisible('taux_conv') && <th className="numeric">Taux conv.</th>}
                        {isTopMetricVisible('conv') && <th className="numeric">Conv.</th>}
                        {isTopMetricVisible('cpa') && <th className="numeric">CPA</th>}
                        {isTopMetricVisible('valeur_conv') && <th className="numeric">Valeur Conv.</th>}
                        {isTopMetricVisible('cos') && <th className="numeric">COS</th>}
                        {isTopMetricVisible('panier_moyen') && <th className="numeric">Panier moy.</th>}
                        {isTopMetricVisible('roas') && <th className="numeric">ROAS</th>}
                        {isTopMetricVisible('roi') && <th className="numeric">ROI</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTopCategories.length === 0 ? (
                        <tr>
                          <td colSpan={1 + (isTopMetricVisible('impressions') ? 1 : 0) + (isTopMetricVisible('clics') ? 1 : 0) + (isTopMetricVisible('ctr') ? 1 : 0) + (isTopMetricVisible('cpc') ? 1 : 0) + (isTopMetricVisible('cout') ? 1 : 0) + (isTopMetricVisible('taux_conv') ? 1 : 0) + (isTopMetricVisible('conv') ? 1 : 0) + (isTopMetricVisible('cpa') ? 1 : 0) + (isTopMetricVisible('valeur_conv') ? 1 : 0) + (isTopMetricVisible('cos') ? 1 : 0) + (isTopMetricVisible('panier_moyen') ? 1 : 0) + (isTopMetricVisible('roas') ? 1 : 0) + (isTopMetricVisible('roi') ? 1 : 0)} style={{ textAlign: 'center', color: '#666' }}>
                            Aucune donnée disponible pour la période sélectionnée
                          </td>
                        </tr>
                      ) : (
                        filteredTopCategories.map((category, index) => {
                          // Trouver la catégorie correspondante dans les données de la période précédente
                          const prevCategory = compareEnabled && previousTopCategories.length > 0
                            ? previousTopCategories.find(p => p.category === category.category)
                            : null;
                          const hasComparison = !!prevCategory;
                          const prevConversionRate = prevCategory && prevCategory.clicks > 0 ? (prevCategory.conversions / prevCategory.clicks * 100) : 0;
                          const prevCpa = prevCategory && prevCategory.conversions > 0 ? (prevCategory.cost / prevCategory.conversions) : 0;
                          const prevCos = prevCategory && prevCategory.conversionsValue > 0 ? (prevCategory.cost / prevCategory.conversionsValue * 100) : 0;

                          const ctr = category.impressions > 0 ? (category.clicks / category.impressions * 100) : 0;
                          const cpc = category.clicks > 0 ? (category.cost / category.clicks) : 0;
                          const roas = category.cost > 0 ? (category.conversionsValue / category.cost) : 0;
                          const roi = (category.cost > 0 && category.conversionsValue > 0) ? ((category.conversionsValue - category.cost) / category.cost * 100) : 0;
                          const prevCtr = prevCategory && prevCategory.impressions > 0 ? (prevCategory.clicks / prevCategory.impressions * 100) : 0;
                          const prevCpc = prevCategory && prevCategory.clicks > 0 ? (prevCategory.cost / prevCategory.clicks) : 0;
                          const prevRoas = prevCategory && prevCategory.cost > 0 ? (prevCategory.conversionsValue / prevCategory.cost) : 0;
                          const prevRoi = prevCategory && prevCategory.cost > 0 && prevCategory.conversionsValue > 0 ? ((prevCategory.conversionsValue - prevCategory.cost) / prevCategory.cost * 100) : 0;

                          return (
                            <tr key={index}>
                              <td style={{ maxWidth: '200px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                {category.category || 'Catégorie non spécifiée'}
                              </td>
                              {isTopMetricVisible('impressions') && (
                                <td className="numeric">
                                  {formatNumber(category.impressions || 0)}
                                  {hasComparison && renderVariation(calculateVariation(category.impressions || 0, prevCategory?.impressions || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('clics') && (
                                <td className="numeric">
                                  {formatNumber(category.clicks || 0)}
                                  {hasComparison && renderVariation(calculateVariation(category.clicks || 0, prevCategory.clicks || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('ctr') && (
                                <td className="numeric">
                                  {formatNumber(ctr, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(ctr, prevCtr))}
                                </td>
                              )}
                              {isTopMetricVisible('cpc') && (
                                <td className="numeric">
                                  €{formatNumber(cpc, 2)}
                                  {hasComparison && renderVariation(calculateVariation(cpc, prevCpc), true)}
                                </td>
                              )}
                              {isTopMetricVisible('cout') && (
                                <td className="numeric">
                                  €{formatNumber(category.cost || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(category.cost || 0, prevCategory?.cost || 0), true)}
                                </td>
                              )}
                              {isTopMetricVisible('taux_conv') && (
                                <td className="numeric">
                                  {formatNumber(category.conversionRate || 0, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(category.conversionRate || 0, prevConversionRate))}
                                </td>
                              )}
                              {isTopMetricVisible('conv') && (
                                <td className="numeric">
                                  {formatNumber(category.conversions || 0, 1)}
                                  {hasComparison && renderVariation(calculateVariation(category.conversions || 0, prevCategory.conversions || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('cpa') && (
                                <td className={`numeric ${getCpaClassName(category.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                  {category.conversions > 0 ? `€${formatNumber(category.cpa || 0, 2)}` : '∞'}
                                  {hasComparison && renderVariation(calculateVariation(category.cpa || 0, prevCpa), true)}
                                </td>
                              )}
                              {isTopMetricVisible('valeur_conv') && (
                                <td className="numeric">
                                  €{formatNumber(category.conversionsValue || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(category.conversionsValue || 0, prevCategory?.conversionsValue || 0))}
                                </td>
                              )}
                              {isTopMetricVisible('cos') && (
                                <td className={`numeric ${getCosClassName(category.cos, selectedAccount?.id || 'global', moduleId)}`}>
                                  {formatNumber(category.cos || 0, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(category.cos || 0, prevCos), true)}
                                </td>
                              )}
                              {isTopMetricVisible('panier_moyen') && (
                                <td className="numeric">
                                  €{formatNumber(category.conversions > 0 ? (category.conversionsValue || 0) / category.conversions : 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(
                                    category.conversions > 0 ? (category.conversionsValue || 0) / category.conversions : 0,
                                    (prevCategory?.conversionsValue || 0) / prevCategory.conversions
                                  ))}
                                </td>
                              )}
                              {isTopMetricVisible('roas') && (
                                <td className="numeric">
                                  {formatNumber(roas, 2)}
                                  {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
                                </td>
                              )}
                              {isTopMetricVisible('roi') && (
                                <td className="numeric">
                                  {formatNumber(roi, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {filteredTopCategories.length > 0 && topTotals && (() => {
                      const totalCtr = topTotals.impressions > 0 ? (topTotals.clicks / topTotals.impressions * 100) : 0;
                      const totalCpc = topTotals.clicks > 0 ? (topTotals.cost / topTotals.clicks) : 0;
                      const totalRoas = topTotals.cost > 0 ? (topTotals.conversionsValue / topTotals.cost) : 0;
                      const totalRoi = topTotals.cost > 0 ? ((topTotals.conversionsValue - topTotals.cost) / topTotals.cost * 100) : 0;
                      const prevCtr = previousTopTotals && previousTopTotals.impressions > 0 ? (previousTopTotals.clicks / previousTopTotals.impressions * 100) : 0;
                      const prevCpc = previousTopTotals && previousTopTotals.clicks > 0 ? (previousTopTotals.cost / previousTopTotals.clicks) : 0;
                      const prevRoas = previousTopTotals && previousTopTotals.cost > 0 ? (previousTopTotals.conversionsValue / previousTopTotals.cost) : 0;
                      const prevRoi = previousTopTotals && previousTopTotals.cost > 0 ? ((previousTopTotals.conversionsValue - previousTopTotals.cost) / previousTopTotals.cost * 100) : 0;
                      return (
                      <tfoot>
                        <tr className="totals-row">
                          <td><strong>TOTAL</strong></td>
                          {isTopMetricVisible('impressions') && (
                            <td className="numeric">
                              <strong>{formatNumber(topTotals.impressions)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.impressions, previousTopTotals.impressions))}
                            </td>
                          )}
                          {isTopMetricVisible('clics') && (
                            <td className="numeric">
                              <strong>{formatNumber(topTotals.clicks)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.clicks, previousTopTotals.clicks))}
                            </td>
                          )}
                          {isTopMetricVisible('ctr') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalCtr, 2)}%</strong>
                              {previousTopTotals && renderVariation(calculateVariation(totalCtr, prevCtr))}
                            </td>
                          )}
                          {isTopMetricVisible('cpc') && (
                            <td className="numeric">
                              <strong>€{formatNumber(totalCpc, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(totalCpc, prevCpc), true)}
                            </td>
                          )}
                          {isTopMetricVisible('cout') && (
                            <td className="numeric">
                              <strong>€{formatNumber(topTotals.cost, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.cost, previousTopTotals.cost), true)}
                            </td>
                          )}
                          {isTopMetricVisible('taux_conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(topTotals.conversionRate, 2)}%</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.conversionRate, previousTopTotals.conversionRate))}
                            </td>
                          )}
                          {isTopMetricVisible('conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(topTotals.conversions, 1)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.conversions, previousTopTotals.conversions))}
                            </td>
                          )}
                          {isTopMetricVisible('cpa') && (
                            <td className={`numeric ${getCpaClassName(topTotals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{topTotals.conversions > 0 ? `€${formatNumber(topTotals.cpa, 2)}` : '∞'}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.cpa, previousTopTotals.cpa), true)}
                            </td>
                          )}
                          {isTopMetricVisible('valeur_conv') && (
                            <td className="numeric">
                              <strong>€{formatNumber(topTotals.conversionsValue, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.conversionsValue, previousTopTotals.conversionsValue))}
                            </td>
                          )}
                          {isTopMetricVisible('cos') && (
                            <td className={`numeric ${getCosClassName(topTotals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{formatNumber(topTotals.cos, 1)}%</strong>
                              {previousTopTotals && renderVariation(calculateVariation(topTotals.cos, previousTopTotals.cos), true)}
                            </td>
                          )}
                          {isTopMetricVisible('panier_moyen') && (
                            <td className="numeric">
                              <strong>€{formatNumber(topTotals.conversions > 0 ? topTotals.conversionsValue / topTotals.conversions : 0, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(
                                topTotals.conversions > 0 ? topTotals.conversionsValue / topTotals.conversions : 0,
                                previousTopTotals.conversionsValue / previousTopTotals.conversions
                              ))}
                            </td>
                          )}
                          {isTopMetricVisible('roas') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoas, 2)}</strong>
                              {previousTopTotals && renderVariation(calculateVariation(totalRoas, prevRoas))}
                            </td>
                          )}
                          {isTopMetricVisible('roi') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoi, 1)}%</strong>
                              {previousTopTotals && renderVariation(calculateVariation(totalRoi, prevRoi))}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    );})()}
                  </table>
                </div>
              </div>

              {/* Flop Catégories */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 15px 0' }}>
                  <h5 style={{ margin: 0, color: '#d32f2f', fontSize: '1.1em' }}>
                    <TrendingDown size={16} />&nbsp;Flop 5 Catégories (Plus cliquées sans vente)
                  </h5>
                  {userRole !== 'CLIENT' && (
                    <button
                      onClick={() => setShowMetricsConfigFlop(true)}
                      className="metrics-settings-btn"
                      title="Configurer les métriques"
                      style={{ padding: '4px', borderRadius: '4px' }}
                    >
                      <Settings size={14} />
                    </button>
                  )}
                </div>
                <div className="campaigns-table-wrapper">
                  <table className="campaigns-table" key={selectedMetrics.join(",")} style={{ tableLayout: "auto" }}>
                    <thead>
                      <tr>
                        <th>Catégorie</th>
                        {isFlopMetricVisible('impressions') && <th className="numeric">Impr.</th>}
                        {isFlopMetricVisible('clics') && <th className="numeric">Clics</th>}
                        {isFlopMetricVisible('ctr') && <th className="numeric">CTR</th>}
                        {isFlopMetricVisible('cpc') && <th className="numeric">CPC</th>}
                        {isFlopMetricVisible('cout') && <th className="numeric">Coût</th>}
                        {isFlopMetricVisible('taux_conv') && <th className="numeric">Taux conv.</th>}
                        {isFlopMetricVisible('conv') && <th className="numeric">Conv.</th>}
                        {isFlopMetricVisible('cpa') && <th className="numeric">CPA</th>}
                        {isFlopMetricVisible('valeur_conv') && <th className="numeric">Valeur Conv.</th>}
                        {isFlopMetricVisible('cos') && <th className="numeric">COS</th>}
                        {isFlopMetricVisible('panier_moyen') && <th className="numeric">Panier moy.</th>}
                        {isFlopMetricVisible('roas') && <th className="numeric">ROAS</th>}
                        {isFlopMetricVisible('roi') && <th className="numeric">ROI</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFlopCategories.length === 0 ? (
                        <tr>
                          <td colSpan={1 + (isFlopMetricVisible('impressions') ? 1 : 0) + (isFlopMetricVisible('clics') ? 1 : 0) + (isFlopMetricVisible('ctr') ? 1 : 0) + (isFlopMetricVisible('cpc') ? 1 : 0) + (isFlopMetricVisible('cout') ? 1 : 0) + (isFlopMetricVisible('taux_conv') ? 1 : 0) + (isFlopMetricVisible('conv') ? 1 : 0) + (isFlopMetricVisible('cpa') ? 1 : 0) + (isFlopMetricVisible('valeur_conv') ? 1 : 0) + (isFlopMetricVisible('cos') ? 1 : 0) + (isFlopMetricVisible('panier_moyen') ? 1 : 0) + (isFlopMetricVisible('roas') ? 1 : 0) + (isFlopMetricVisible('roi') ? 1 : 0)} style={{ textAlign: 'center', color: '#666' }}>
                            Aucune donnée disponible pour la période sélectionnée
                          </td>
                        </tr>
                      ) : (
                        filteredFlopCategories.map((category, index) => {
                          const prevCategory = compareEnabled && previousFlopCategories.length > 0
                            ? previousFlopCategories.find(p => p.category === category.category)
                            : null;
                          const hasComparison = !!prevCategory;
                          const ctr = category.impressions > 0 ? (category.clicks / category.impressions * 100) : 0;
                          const cpc = category.clicks > 0 ? (category.cost / category.clicks) : 0;
                          const convRate = category.clicks > 0 ? (category.conversions / category.clicks * 100) : 0;
                          const roas = category.cost > 0 ? (category.conversionsValue / category.cost) : 0;
                          const roi = (category.cost > 0 && category.conversionsValue > 0) ? ((category.conversionsValue - category.cost) / category.cost * 100) : 0;
                          const prevCtr = prevCategory && prevCategory.impressions > 0 ? (prevCategory.clicks / prevCategory.impressions * 100) : 0;
                          const prevCpc = prevCategory && prevCategory.clicks > 0 ? (prevCategory.cost / prevCategory.clicks) : 0;
                          const prevConvRate = prevCategory && prevCategory.clicks > 0 ? (prevCategory.conversions / prevCategory.clicks * 100) : 0;
                          const prevCpa = prevCategory && prevCategory.conversions > 0 ? (prevCategory.cost / prevCategory.conversions) : 0;
                          const prevCos = prevCategory && prevCategory.conversionsValue > 0 ? (prevCategory.cost / prevCategory.conversionsValue * 100) : 0;
                          const prevRoas = prevCategory && prevCategory.cost > 0 ? (prevCategory.conversionsValue / prevCategory.cost) : 0;
                          const prevRoi = prevCategory && prevCategory.cost > 0 && prevCategory.conversionsValue > 0 ? ((prevCategory.conversionsValue - prevCategory.cost) / prevCategory.cost * 100) : 0;

                          return (
                            <tr key={index}>
                              <td style={{ maxWidth: '200px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                {category.category || 'Catégorie non spécifiée'}
                              </td>
                              {isFlopMetricVisible('impressions') && (
                                <td className="numeric">
                                  {formatNumber(category.impressions || 0)}
                                  {hasComparison && renderVariation(calculateVariation(category.impressions || 0, prevCategory?.impressions || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('clics') && (
                                <td className="numeric">
                                  {formatNumber(category.clicks || 0)}
                                  {hasComparison && renderVariation(calculateVariation(category.clicks || 0, prevCategory.clicks || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('ctr') && (
                                <td className="numeric">
                                  {formatNumber(ctr, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(ctr, prevCtr))}
                                </td>
                              )}
                              {isFlopMetricVisible('cpc') && (
                                <td className="numeric">
                                  €{formatNumber(cpc, 2)}
                                  {hasComparison && renderVariation(calculateVariation(cpc, prevCpc), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('cout') && (
                                <td className="numeric">
                                  €{formatNumber(category.cost || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(category.cost || 0, prevCategory?.cost || 0), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('taux_conv') && (
                                <td className="numeric">
                                  {formatNumber(convRate, 2)}%
                                  {hasComparison && renderVariation(calculateVariation(convRate, prevConvRate))}
                                </td>
                              )}
                              {isFlopMetricVisible('conv') && (
                                <td className="numeric">
                                  {formatNumber(category.conversions || 0, 1)}
                                  {hasComparison && renderVariation(calculateVariation(category.conversions || 0, prevCategory?.conversions || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('cpa') && (
                                <td className={`numeric ${getCpaClassName(category.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                  {category.conversions > 0 ? `€${formatNumber(category.cpa || 0, 2)}` : '∞'}
                                  {hasComparison && renderVariation(calculateVariation(category.cpa || 0, prevCpa), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('valeur_conv') && (
                                <td className="numeric">
                                  €{formatNumber(category.conversionsValue || 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(category.conversionsValue || 0, prevCategory?.conversionsValue || 0))}
                                </td>
                              )}
                              {isFlopMetricVisible('cos') && (
                                <td className={`numeric ${getCosClassName(category.cos, selectedAccount?.id || 'global', moduleId)}`}>
                                  {formatNumber(category.cos || 0, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(category.cos || 0, prevCos), true)}
                                </td>
                              )}
                              {isFlopMetricVisible('panier_moyen') && (
                                <td className="numeric">
                                  €{formatNumber(category.conversions > 0 ? (category.conversionsValue || 0) / category.conversions : 0, 2)}
                                  {hasComparison && renderVariation(calculateVariation(
                                    category.conversions > 0 ? (category.conversionsValue || 0) / category.conversions : 0,
                                    (prevCategory?.conversionsValue || 0) / prevCategory.conversions
                                  ))}
                                </td>
                              )}
                              {isFlopMetricVisible('roas') && (
                                <td className="numeric">
                                  {formatNumber(roas, 2)}
                                  {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
                                </td>
                              )}
                              {isFlopMetricVisible('roi') && (
                                <td className="numeric">
                                  {formatNumber(roi, 1)}%
                                  {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {filteredFlopCategories.length > 0 && flopTotals && (() => {
                      const totalCtr = flopTotals.impressions > 0 ? (flopTotals.clicks / flopTotals.impressions * 100) : 0;
                      const totalConvRate = flopTotals.clicks > 0 ? (flopTotals.conversions / flopTotals.clicks * 100) : 0;
                      const totalRoas = flopTotals.cost > 0 ? (flopTotals.conversionsValue / flopTotals.cost) : 0;
                      const totalRoi = flopTotals.cost > 0 ? ((flopTotals.conversionsValue - flopTotals.cost) / flopTotals.cost * 100) : 0;
                      const prevCtr = previousFlopTotals && previousFlopTotals.impressions > 0 ? (previousFlopTotals.clicks / previousFlopTotals.impressions * 100) : 0;
                      const prevConvRate = previousFlopTotals && previousFlopTotals.clicks > 0 ? (previousFlopTotals.conversions / previousFlopTotals.clicks * 100) : 0;
                      const prevRoas = previousFlopTotals && previousFlopTotals.cost > 0 ? (previousFlopTotals.conversionsValue / previousFlopTotals.cost) : 0;
                      const prevRoi = previousFlopTotals && previousFlopTotals.cost > 0 ? ((previousFlopTotals.conversionsValue - previousFlopTotals.cost) / previousFlopTotals.cost * 100) : 0;
                      return (
                      <tfoot>
                        <tr className="totals-row">
                          <td><strong>TOTAL</strong></td>
                          {isFlopMetricVisible('impressions') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopTotals.impressions)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.impressions, previousFlopTotals.impressions))}
                            </td>
                          )}
                          {isFlopMetricVisible('clics') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopTotals.clicks)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.clicks, previousFlopTotals.clicks))}
                            </td>
                          )}
                          {isFlopMetricVisible('ctr') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalCtr, 2)}%</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(totalCtr, prevCtr))}
                            </td>
                          )}
                          {isFlopMetricVisible('cpc') && (
                            <td className="numeric">
                              <strong>€{formatNumber(flopTotals.cpc, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.cpc, previousFlopTotals.cpc), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('cout') && (
                            <td className="numeric">
                              <strong>€{formatNumber(flopTotals.cost, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.cost, previousFlopTotals.cost), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('taux_conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalConvRate, 2)}%</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(totalConvRate, prevConvRate))}
                            </td>
                          )}
                          {isFlopMetricVisible('conv') && (
                            <td className="numeric">
                              <strong>{formatNumber(flopTotals.conversions, 1)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.conversions, previousFlopTotals.conversions))}
                            </td>
                          )}
                          {isFlopMetricVisible('cpa') && (
                            <td className={`numeric ${getCpaClassName(flopTotals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{flopTotals.conversions > 0 ? `€${formatNumber(flopTotals.cpa, 2)}` : '∞'}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.cpa, previousFlopTotals.cpa), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('valeur_conv') && (
                            <td className="numeric">
                              <strong>€{formatNumber(flopTotals.conversionsValue, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.conversionsValue, previousFlopTotals.conversionsValue))}
                            </td>
                          )}
                          {isFlopMetricVisible('cos') && (
                            <td className={`numeric ${getCosClassName(flopTotals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                              <strong>{formatNumber(flopTotals.cos, 1)}%</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(flopTotals.cos, previousFlopTotals.cos), true)}
                            </td>
                          )}
                          {isFlopMetricVisible('panier_moyen') && (
                            <td className="numeric">
                              <strong>€{formatNumber(flopTotals.conversions > 0 ? flopTotals.conversionsValue / flopTotals.conversions : 0, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(
                                flopTotals.conversions > 0 ? flopTotals.conversionsValue / flopTotals.conversions : 0,
                                previousFlopTotals.conversionsValue / previousFlopTotals.conversions
                              ))}
                            </td>
                          )}
                          {isFlopMetricVisible('roas') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoas, 2)}</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(totalRoas, prevRoas))}
                            </td>
                          )}
                          {isFlopMetricVisible('roi') && (
                            <td className="numeric">
                              <strong>{formatNumber(totalRoi, 1)}%</strong>
                              {previousFlopTotals && renderVariation(calculateVariation(totalRoi, prevRoi))}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    );})()}
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de configuration des métriques pour Top */}
      <MetricsConfigModal
        isOpen={showMetricsConfigTop}
        onClose={() => setShowMetricsConfigTop(false)}
        componentId="topflop_categories_top"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Top 5 Catégories (Plus vendeuses)"
        hideApplyToAll={true}
      />

      {/* Modal de configuration des métriques pour Flop */}
      <MetricsConfigModal
        isOpen={showMetricsConfigFlop}
        onClose={() => setShowMetricsConfigFlop(false)}
        componentId="topflop_categories_flop"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Flop 5 Catégories (Plus cliquées sans vente)"
        hideApplyToAll={true}
      />
    </div>
  );
});

TopFlopCategoriesTableModular.displayName = 'TopFlopCategoriesTableModular';

export default TopFlopCategoriesTableModular;
